import { Button, Card, Checkbox, Col, InputNumber, Pagination, Row, Select, Space, Tag } from "antd"
import CardTitle from "./CardTitle"
import CollapsingCard from "./CollapsingCard"
import { DownloadOutlined, ExportOutlined, FileImageFilled, FireOutlined, MinusOutlined, PlusOutlined, ReloadOutlined, RocketFilled } from "@ant-design/icons"
import TinyPreview from "./TinyPreview"
import { useCallback, useEffect, useRef, useState } from "react"
import { fireEvent, uploadFile, useEventListener, useInterval } from "../util"
import { fetchAuth } from "../authentication"
import { fetchModelHistory } from "../api"
import { ImageThumbGrid } from "./MediaBrowser"

// instead of the static import
// const poepen = import('@khronosgroup/gltf-viewer/dist/gltf-viewer.module');
import { GltfView, GltfState, ResourceLoader } from '@khronosgroup/gltf-viewer';
import TextArea from "antd/es/input/TextArea"

// console.log(poepen);
// import { useGLTF } from '@react-three/drei'

export const ModelGenerator = (props) => {
    const [queueSize, setQueueSize] = useState(0);
    const [currentModel, setCurrentModel] = useState(null);

    return (<>
        <Col lg={12} sm={24}>
            <Row gutter={[12, 12]}>
                <Col span={24}>
                    <ModelGenerationForm queueSize={queueSize} />
                </Col>

                <Col span={24}>
                    <GenerationQueue setQueueSize={setQueueSize} />
                </Col>
            </Row>
        </Col>

        <Col lg={12} sm={24} >
            <Row gutter={[12, 12]}>
                <Col span={24}>
                    <IframeModelViewer model={currentModel} />
                    {/* <ModelViewer model={currentModel} /> */}
                </Col>
                <Col span={24}>
                    <MediaBrowser3D
                        selectImage={(aa) => {
                            fireEvent('model3d.select', aa);
                            setCurrentModel(aa?.result);
                        }}
                    />
                </Col>
            </Row>
        </Col>
    </>)
}



const submitModelTask = async (generateParams) => {

    const r = await fetchAuth('https://ctlzr-api.bitgate.workers.dev/api/v1/jobs/img2model', {
        method: 'PUT',
        body: JSON.stringify([generateParams]),
        headers: {
            "content-type": "application/json"
        },
    });

    if (!r.ok) {
        window.alert("Failed!! OH NO! status = " + r.statusText);
        return;
    }

    const _resp = await r.json();
    console.log(_resp);
};

const ModelGenerationForm = (props) => {
    const { queueSize } = props;

    const [baseImage, setBaseImage] = useState(null);
    const [cfg, setCfg] = useState(5.0);
    const [cfgInpaint, setCfgInpaint] = useState(3.0);
    const [downsample, setDownsample] = useState(true);
    const [steps, setSteps] = useState(50);
    const [inpaintSteps, setInpaintSteps] = useState(15); // UniPC
    const [seed, setSeed] = useState(42);
    const [numChunks, setNumChunks] = useState(200000);
    const [octreeResolution, setOctreeResolution] = useState(256);
    const [remeshFaceCount, setRemeshFaceCount] = useState(8000);
    const [remeshAggression, setRemeshAggression] = useState(7);
    const [resizeInput, setResizeInput] = useState(true);
    const [downscaleImage, setDownscaleImage] = useState(true);
    const [numViews, setNumViews] = useState(6);
    const [paintResolution, setPaintResolution] = useState(512);
    const [inpaintRenderSize, setInpaintRenderSize] = useState(1024 * 2);
    const [inpaintTextureSize, setInpaintTextureSize] = useState(1024 * 4);
    const [diffusionCaption, setDiffusionCaption] = useState('');
    const [pbrWeight, setPbrWeight] = useState(1.0);
    const [promptWeight, setPromptWeight] = useState(1.0);


    const generateParams = {
        image_url: baseImage,
        guidance_scale: cfg,
        inpaint_guidance_scale: cfgInpaint,
        downsample_textures: downsample,
        steps,
        inpaint_inference_steps: inpaintSteps,
        seed: seed || undefined,
        num_chunks: numChunks,
        octree_resolution: octreeResolution,
        remesh_face_count: remeshFaceCount,
        remesh_aggression: remeshAggression,
        resize_input: resizeInput,
        downscale_image: downscaleImage,
        num_views: numViews,
        paint_resolution: paintResolution,
        inpaint_render_size: inpaintRenderSize,
        inpaint_texture_size: inpaintTextureSize,
        diffusion_caption: diffusionCaption || null,
        pbr_prompt_weight: pbrWeight,
        text_prompt_weight: promptWeight,
    };

    // bake_mode = params.get("bake_mode", "back_sample")


    const loadArgs = args => {
        if (args.image_url) {
            setBaseImage(args.image_url);
        }

        setCfg(args.guidance_scale);
        setCfgInpaint(args.inpaint_guidance_scale || 3.0);
        setSteps(args.steps);
        setDownsample(args.downsample_textures);
        if (args.seed) setSeed(args.seed);
        setNumChunks(args.num_chunks);
        setOctreeResolution(args.octree_resolution);
        setRemeshFaceCount(args.remesh_face_count);
        setRemeshAggression(args.remesh_aggression);
        setResizeInput(args.resize_input);
        setDownscaleImage(args.downscale_image || true);
        setNumViews(args.num_views || 6);
        setPaintResolution(args.paint_resolution || 512);
        setInpaintRenderSize(args.inpaint_render_size || (1024 * 2));
        setInpaintTextureSize(args.inpaint_texture_size || (1024 * 4));
        setDiffusionCaption(args.diffusion_caption || '');
        setInpaintSteps(args.inpaint_inference_steps || 15);
        setPbrWeight(args.pbr_prompt_weight || 1.0);
        setPromptWeight(args.text_prompt_weight || 1.0);
    }

    useEventListener("model3d.select", (args) => {
        loadArgs(args.params);
    })

    return (
        <CollapsingCard title={<CardTitle icon={<FileImageFilled />}>3D Model Generation</CardTitle>} size="small" bodyStyle={{ background: '#262a33', maxHeight: '90vh', overflowY: 'auto' }} style={{ background: '#0e1015', border: '1px solid #343d4a', marginBottom: 8 }}>
            <form onSubmitCapture={e => { e.preventDefault(); submitModelTask(generateParams); return false; }} style={{ padding: 8 }}>
                <Row span={24}>
                    <Col lg={6} xs={24}>
                        <ImageWithUploader src={baseImage} setSrc={setBaseImage} />

                        <TextArea value={diffusionCaption} onChange={(ev) => setDiffusionCaption(ev.currentTarget.value)} />
                    </Col>

                    <Col lg={18} xs={24}>
                        <Row gutter={16}>
                            <Col span={4} xs={8}>
                                <div className="inference-prop-label">Steps</div>
                                <PreciseNumberInput value={steps} setValue={setSteps} min={1} max={100} step={5} float={false} />
                            </Col>
                            <Col span={4} xs={8}>
                                <div className="inference-prop-label">CFG</div>
                                <PreciseNumberInput value={cfg} setValue={setCfg} min={1} max={20} step={0.5} />
                            </Col>
                            <Col span={4} xs={8}>
                                <div className="inference-prop-label">PCFG</div>
                                <PreciseNumberInput value={cfgInpaint} setValue={setCfgInpaint} min={1} max={20} step={0.5} />
                            </Col>
                            <Col span={4} xs={8}>
                                <div className="inference-prop-label">Views</div>
                                <PreciseNumberInput value={numViews} setValue={setNumViews} min={1} max={12} step={1} float={false} />
                            </Col>
                            <Col span={4} xs={8}>
                                <div className="inference-prop-label">PBR weight</div>
                                <PreciseNumberInput value={pbrWeight} setValue={setPbrWeight} min={-10} max={10} step={0.05} float={true} />
                            </Col>
                            <Col span={4} xs={8}>
                                <div className="inference-prop-label">Prompt weight</div>
                                <PreciseNumberInput value={promptWeight} setValue={setPromptWeight} min={-10} max={10} step={0.05} float={true} />
                            </Col>
                        </Row>

                        <Row gutter={16} style={{ marginTop: 16 }}>
                            <Col span={8}>
                                <div className="inference-prop-label">Num Chunks</div>
                                <PreciseNumberInput value={numChunks} setValue={setNumChunks} min={1000} max={1000000} step={10000} float={false} />
                            </Col>
                            <Col span={8}>
                                <div className="inference-prop-label">Octree Resolution</div>
                                <PreciseNumberInput value={octreeResolution} setValue={setOctreeResolution} min={32} max={1024} step={16} float={false} />
                            </Col>

                            <Col span={8}>
                                <div className="inference-prop-label">Remesh Face Count</div>
                                <PreciseNumberInput value={remeshFaceCount} setValue={setRemeshFaceCount} min={1} max={200000} float={false} step={250} />
                            </Col>
                            <Col span={8}>
                                <div className="inference-prop-label">Remesh Aggression</div>
                                <PreciseNumberInput value={remeshAggression} setValue={setRemeshAggression} min={1} max={10} step={1} float={false} />
                            </Col>
                        </Row>

                        <Row gutter={16} style={{ marginTop: 16 }}>
                            <Col span={8}>
                                <div className="inference-prop-label">Inpaint Steps</div>
                                <PreciseNumberInput value={inpaintSteps} setValue={setInpaintSteps} min={1} max={50} step={5} float={false} />
                            </Col>
                            <Col span={8}>
                                <div className="inference-prop-label">Inpaint Resolution</div>
                                <PreciseNumberInput value={paintResolution} setValue={setPaintResolution} min={128} max={1024 * 16} step={128} float={false} />
                            </Col>
                            <Col span={8}>
                                <div className="inference-prop-label">Inpaint Render Size</div>
                                <PreciseNumberInput value={inpaintRenderSize} setValue={setInpaintRenderSize} min={128} max={1024 * 16} step={128} float={false} />
                            </Col>
                            <Col span={8}>
                                <div className="inference-prop-label">Inpaint Texture Size</div>
                                <PreciseNumberInput value={inpaintTextureSize} setValue={setInpaintTextureSize} min={128} max={1024 * 16} step={128} float={false} />
                            </Col>
                        </Row>

                        <Row style={{ padding: 12 }}>
                            <Col span={24}>
                                <Checkbox checked={downsample} onChange={e => setDownsample(e.target.checked)}>Downsample Textures</Checkbox>
                                <Checkbox checked={resizeInput} onChange={e => setResizeInput(e.target.checked)} style={{ marginLeft: 16 }}>Resize Input</Checkbox>
                                <Checkbox checked={downscaleImage} onChange={e => setDownscaleImage(e.target.checked)} style={{ marginLeft: 16 }}>Downscale Image</Checkbox>
                            </Col>
                        </Row>
                    </Col>
                </Row>


                <Space style={{ float: 'right' }}>
                    <Col span={5} xs={6} flex style={{ display: 'flex', flexDirection: 'row' }}>
                        <Space direction="vertical">
                            <div className="inference-prop-label">Seed</div>
                            <Space direction="horizontal">
                                <InputNumber style={{ minWidth: 120 }} value={seed} onChange={e => setSeed(Number(e))} />
                                <Button style={{ minWidth: 32 }} type="default" onClick={() => setSeed((Math.random() * 2147000000) | 0)} icon={<ReloadOutlined />} />
                            </Space>
                        </Space>
                    </Col>

                    <Button type="primary" htmlType="submit" icon={<RocketFilled />} style={{ minWidth: 32, marginTop: 16, marginBottom: 16 }}>
                        Generate ({queueSize})
                    </Button>
                </Space>
            </form>
        </CollapsingCard>
    );
}


const GenerationQueue = ({ setQueueSize }) => {
    const [queue, setQueue] = useState([]);

    const refresh = () => {
        fetch('https://ctlzr-api.bitgate.workers.dev/api/v1/jobs/img2model/all')
            .then(r => r.json()).then(data => {
                setQueue(data);
                setQueueSize?.(data.filter(e => !e.result).length);
            });
    }

    const inQueue = queue.filter(e => !e.result);

    useEffect(() => {
        refresh();

        const key = window.setInterval(refresh, 10000);
        return () => window.clearInterval(key);
    }, []);

    return (
        <CollapsingCard title={<>Model Generation queue ({inQueue.length})</>} size="small" bodyStyle={{ background: '#262a33', maxHeight: '90vh', overflowY: 'auto' }} style={{ background: '#0e1015', border: '1px solid #343d4a', marginBottom: 8 }}>
            <Space direction="vertical">
                {
                    inQueue.map(q => (<pre style={{ overflowX: 'auto', maxWidth: 320 }}>{JSON.stringify(q)}</pre>))
                }
            </Space>
        </CollapsingCard>
    )
}

const ImageWithUploader = (props) => {
    const { src, setSrc } = props;

    return (
        <Col className="base-file-wrapper" flex={'1 1'} style={{ alignSelf: 'stretch' }} onDrop={e => { e.preventDefault(); uploadFile(e, url => setSrc(url)); }} onDragOver={e => e.preventDefault()}>
            <TinyPreview style={{ aspectRatio: '1/1' }} url={src} remove={() => setSrc(null)} onClick={() => setSrc(window.prompt('Enter image URL'))}>
                <label htmlFor="base-image-file" className="file-uploader" />
                <input id="base-image-file" type="file" name="base-image-file" className="invisible-file-input" onChange={e => uploadFile(e, setSrc)} accept="image/png, image/jpeg, image/webp, image/bmp" />
            </TinyPreview>
        </Col>
    )
}

const PreciseNumberInput = (props) => {
    const { value, setValue, min, max, step = 0.5, float = true } = props;
    return (
        <InputNumber
            min={min}
            max={max}
            step={step}

            precision={float ? 1 : 0}
            value={value}
            onChange={setValue}
            style={{ width: '100%', fontSize: 13, paddingLeft: 0, paddingRight: 0 }}

            addonAfter={<Button icon={<PlusOutlined />} onClick={() => setValue(v => v + step)} />}
            addonBefore={<Button icon={<MinusOutlined />} onClick={() => setValue(v => v - step)} />}
        />
    )
}

const ModelViewer = (props) => {
    const { model } = props;

    const ref = useRef(null);
    const gltf = useRef(null); // We use a ref cos I cba with React man

    useEffect(() => {
        if (ref.current) {
            // Not everyone and everything supports WebGL2 yet (ahem SA FA REEEE)
            let context = ref.current.getContext('webgl2', { alpha: false, antialias: true });

            // If that failed (SA FA REEEEEEEEEEE) then we just try a WebGL1 context, fuck it
            if (!context) {
                context = ref.current.getContext('webgl');
            }

            // Unable to create a context for either WebGL2 nor WebGL1? Gay
            if (!context) {
                return;
            }

            // Ok we got some kind of context, 2 or 1 or 6 or whatever.
            const view = new GltfView(context);

            // Set up state
            // state.animationIndices = [0, 1, 2];
            // state.animationTimer.start();

            // view.createResourceLoader(); // Gay libs

            const loader = new ResourceLoader(view, '');
            console.log("Welcome to my cum show", view);
            console.log(loader);
            gltf.current = { view, state: null, loader, model: null };

            // Load data
            (async () => {
                const modelAsset = await loader.loadGltf(model);
                const envAsset = await loader.loadEnvironment('https://raw.githubusercontent.com/KhronosGroup/glTF-Sample-Environments/low_resolution_hdrs/neutral.hdr');

                console.log(modelAsset);
                console.log(envAsset);

                const state = view.createState();
                state.sceneIndex = 0;
                state.gltf = modelAsset;
                state.environment = envAsset;
                gltf.current.state = state;

                gltf.current.model = modelAsset;
            })();

            // loader.loadGltf(model).then(tt => {
            //     gltf.current.model = tt;

            //     const state = view.createState();
            //     state.sceneIndex = 0;
            //     state.gltf = tt;
            //     state.environment = tt;
            //     gltf.current.state = state;

            //     console.log("modelleke", tt);
            // });

            const stopped = [false];
            const update = () => {
                if (stopped[0]) return;

                // console.log(gltf.current);
                if (ref.current && gltf.current?.state) {
                    gltf.current.view.renderFrame(gltf.current.state, ref.current.width, ref.current.height);
                }

                window.requestAnimationFrame(update);
            };

            window.requestAnimationFrame(update);

            return () => {
                stopped[0] = true;
            }
        }
    }, [ref, ref.current]);

    return (
        <CollapsingCard title={<><span>Model Viewer</span> <Button size="small" type="text" target="_blank" href={"https://github.khronos.org/glTF-Sample-Viewer-Release/?model=" + encodeURIComponent(model)}>External</Button></>} size="small">
            <canvas ref={ref} />
            {/* <SingleModel url={model} /> */}
        </CollapsingCard>
    )
}

const meshToTriKey = {
    final_mesh: "final_mesh_tris",
    remesh: "remesh_tris",
}

const IframeModelViewer = ({ model }) => {
    const [meshKey, setMeshKey] = useState('final_mesh');

    const selectedMesh = model?.[meshKey];
    const externUrl = "https://github.khronos.org/glTF-Sample-Viewer-Release/?model=" + encodeURIComponent(selectedMesh);
    const url = "https://github.khronos.org/glTF-Sample-Viewer-Release/?noUI=true&model=" + encodeURIComponent(selectedMesh);

    const triangles = selectedMesh && meshToTriKey[meshKey] ? model.stats[meshToTriKey[meshKey]] : 0;

    return (
        <CollapsingCard title={<><span>Model Viewer</span></>} size="small">
            <Row>
                <Col flex={'1 1 auto'}>
                    <Select value={[meshKey]} onChange={e => setMeshKey(e)} style={{ minWidth: 120 }}>
                        <Select.Option value="final_mesh">Final mesh</Select.Option>
                        <Select.Option value="shape">Shape</Select.Option>
                        <Select.Option value="remesh">Remeshed shape</Select.Option>
                    </Select>

                    <Button size="small" type="primary" target="_blank" href={externUrl} icon={<ExportOutlined />}>External</Button>
                </Col>

                <Col>
                    <Tag>Triangles: {triangles}</Tag>

                    <Button href={selectedMesh} target="_blank" icon={<DownloadOutlined />} size="small" type="primary" style={{ fontSize: 13 }}>Download</Button>
                </Col>
            </Row>

            {selectedMesh && <iframe title="kak en poep sigaret" src={url} width="100%" style={{ minHeight: 260, aspectRatio: '1/1', maxHeight: 360 }}></iframe>}
        </CollapsingCard>
    )
}

// function SingleModel(props) {
//     const { nodes, materials } = useGLTF(props.url);

//     return (
//         <group {...props} dispose={null}>
//             <mesh
//                 castShadow
//                 receiveShadow
//                 geometry={nodes['final-model'].geometry}
//                 material={materials['Material.001']}
//                 rotation={[Math.PI / 2, 0, 0]}
//             />
//         </group>
//     )
// }

const optimizePreview = (url) => `https://training.bitgate.ai/cdn-cgi/image/format=webp,quality=90,width=256,height=256/${url}`;

const MediaBrowser3D = (props) => {
    const { selectImage } = props;

    const [history, setHistory] = useState([]);
    const [totalMatches, setTotalMatches] = useState(0);
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);
    const [perspective, setPerspective] = useState('combined');

    const updateHistory = useCallback(async () => {
        const q = query.trim();

        let filters = {};

        const { values, total } = await fetchModelHistory(36, filters, q, (page - 1) * 36);
        setHistory(values || []);
        setTotalMatches(total);
    }, [query, page]);

    useInterval(() => updateHistory(), 10000, false, [updateHistory]);

    const images = history.filter(h => h?.img2model?.renders)
        .map(h => ({ ...h.img2model, '_timestamp': h['@timestamp'] }));

    const imagesForList = images.map(e => ({ args: e.params, url: e.renders[perspective + '.png'] }));

    return (
        <CollapsingCard title="Media browser (3D)" size="small">
            <Row>
                <Col flex={'1 1 auto'}>
                    <Select value={[perspective]} onChange={e => setPerspective(e)} style={{ minWidth: 120 }}>
                        <Select.Option value="combined">Combined</Select.Option>
                        <Select.Option value="back">Back</Select.Option>
                        <Select.Option value="front">Front</Select.Option>
                        <Select.Option value="right">Right</Select.Option>
                        <Select.Option value="top">Top</Select.Option>
                    </Select>
                </Col>

                <Col flex={0} style={{ textAlign: 'center', marginBottom: 8 }}>
                    <Pagination
                        position="bottom"
                        total={totalMatches}
                        current={page}
                        pageSize={36}
                        onChange={setPage}
                        showQuickJumper={false}
                        showSizeChanger={false}
                        showTotal={false}
                        align="center"
                    />
                </Col>
            </Row>

            <Row gutter={0} >
                <ImageThumbGrid
                    images={imagesForList}
                    selectImage={(_, url, idx) => selectImage(images[idx], url)}
                    setBaseImage={() => { }}
                    selectionMode={false}
                    pickImage={() => { }}
                    selectedImages={[]}
                    getThumbnail={optimizePreview}
                    getTopRightInfo={(img, idx) => ((images[idx]?.result?.stats?.final_mesh_tris ?? 0) / 1000).toFixed(1) + " k tris"}
                />
            </Row>

        </CollapsingCard>
    )
}